import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { DataUI } from "@/components/data-ui";
import { $getGenuiSpec } from "@/server/actions/genui";

export const Route = createFileRoute("/embed/$lessonId")({
	component: EmbedLesson,
	loader: ({ params }) => ({ lessonId: params.lessonId }),
});

type UIAction =
	| {
			type: "tool";
			messageId?: string;
			payload: { toolName: string; params: Record<string, unknown> };
	  }
	| { type: "prompt"; messageId?: string; payload: { prompt: string } }
	| { type: "link"; messageId?: string; payload: { url: string } }
	| {
			type: "intent";
			messageId?: string;
			payload: { intent: string; params: Record<string, unknown> };
	  }
	| { type: "notify"; messageId?: string; payload: { message: string } };

declare global {
	interface Window {
		studyosPostAction?: (action: UIAction) => void;
	}
}

function postToHost(message: unknown) {
	if (typeof window === "undefined" || window.parent === window) return;
	window.parent.postMessage(message, "*");
}

function EmbedLesson() {
	const { lessonId } = Route.useLoaderData();
	const containerRef = useRef<HTMLDivElement>(null);

	const { data: lesson } = useSuspenseQuery({
		queryKey: ["genui", "spec", lessonId],
		queryFn: () => $getGenuiSpec({ data: { id: lessonId } }),
		staleTime: Infinity,
		// @ts-expect-error — preview.ts uses the same workaround for Spec serialization
		select: (data) => data?.data,
	});

	useEffect(() => {
		window.studyosPostAction = (action) => postToHost(action);
		postToHost({ type: "ui-lifecycle-iframe-ready" });
		return () => {
			delete window.studyosPostAction;
		};
	}, []);

	useEffect(() => {
		if (!containerRef.current) return;
		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				postToHost({
					type: "ui-size-change",
					payload: {
						width: entry.contentRect.width,
						height: entry.contentRect.height,
					},
				});
			}
		});
		observer.observe(containerRef.current);
		return () => observer.disconnect();
	}, []);

	if (!lesson) {
		throw notFound();
	}

	return (
		<div ref={containerRef} className="min-h-screen w-full bg-background p-4">
			<DataUI tree={lesson.spec} isStreaming={false} />
		</div>
	);
}
